import React, { useImperativeHandle, forwardRef } from 'react';
import { Row } from '../../styles/utils/Grid';
import Section from '../Section';
import Input from '../Input';
import {
  required,
  validPhone,
  validPhoneLength,
  validEmail,
  validHomePhone,
  validHomePhoneLength,
  validCommercialPhoneLength,
  validCommercialPhone,
} from '../../utils/inputValidation';
import masks from '../../utils/masks';
import { useValidation, useInputState } from '../../hooks';
import ContactDataFooter from '../ContactDataFooter';
import {
  contactDataTitle,
  mobilePhonePlaceholder,
  commercialPhonePlaceholder,
  residentialPhonePlaceholder,
  emailPlaceholder,
  noPhoneErrorMessage,
} from '../../utils/constants';
import { filterContactArray, findContact } from '../../utils/utils';

const ContactData = ({ contactData }, ref) => {
  const [
    mobilePhone,
    setMobilePhone,
    mobilePhoneRef,
    validateMobilePhone,
  ] = useInputState(findContact('CEL', contactData?.parametros));
  const [
    commercialPhone,
    setComercialPhone,
    comercialPhoneRef,
    validateComercialPhone,
  ] = useInputState(findContact('COM', contactData?.parametros));
  const [
    residentialPhone,
    setResidentialPhone,
    residentialPhoneRef,
    validateResidentialPhone,
  ] = useInputState(findContact('RES', contactData?.parametros));
  const [email, setEmail, emailRef, validateEmail] = useInputState(
    findContact('PES', contactData?.parametros)
  );

  const [contactDataFooterRef, validateContactDataFooter] = useValidation();
  const handleInputChange = setter => value => {
    setter(value);
  };

  useImperativeHandle(
    ref,
    () => ({
      validate: () => {
        const phones = [mobilePhone, commercialPhone, residentialPhone];
        const noPhone = phones.every(obj => !obj.value);

        const invalidEmail = validateEmail();
        const footerDataInfo = validateContactDataFooter();
        if (noPhone) {
          setMobilePhone({
            ...mobilePhone,
            invalid: true,
            errorMessage: noPhoneErrorMessage,
          });
          setResidentialPhone({
            ...residentialPhone,
            invalid: true,
            errorMessage: noPhoneErrorMessage,
          });
          setComercialPhone({
            ...commercialPhone,
            invalid: true,
            errorMessage: noPhoneErrorMessage,
          });

          return { invalid: true, value: '' };
        }

        setMobilePhone({
          ...mobilePhone,
          invalid: false,
        });
        setResidentialPhone({
          ...residentialPhone,
          invalid: false,
        });
        setComercialPhone({
          ...commercialPhone,
          invalid: false,
        });
        const isInvalid =
          [
            validateMobilePhone,
            validateComercialPhone,
            validateResidentialPhone,
          ]
            .reverse()
            .reduce((acc, v) => {
              const invalid = v();
              return acc || invalid;
            }, false) ||
          invalidEmail ||
          footerDataInfo.invalid;

        return {
          invalid: isInvalid,
          value: isInvalid
            ? ''
            : {
                comunicacao: {
                  parametros: filterContactArray([
                    {
                      codigo: 'email',
                      tipo: 'PES',
                      valor: email.value,
                    },
                    {
                      codigo: 'telefone',
                      tipo: 'CEL',
                      valor: mobilePhone.value,
                    },
                    {
                      codigo: 'telefone',
                      tipo: 'COM',
                      valor: commercialPhone.value,
                    },
                    {
                      codigo: 'telefone',
                      tipo: 'RES',
                      valor: residentialPhone.value,
                    },
                  ]),
                  recebimento: [
                    {
                      celular: footerDataInfo.value?.celular ?? false,
                      email: footerDataInfo.value?.email ?? false,
                    },
                  ],
                },
              },
        };
      },
    }),
    [
      validateMobilePhone,
      email.value,
      mobilePhone,
      residentialPhone,
      validateComercialPhone,
      validateEmail,
      validateResidentialPhone,
      commercialPhone,
      validateContactDataFooter,
      setComercialPhone,
      setResidentialPhone,
      setMobilePhone,
    ]
  );

  return (
    <Section title={contactDataTitle}>
      <Row columns={2}>
        <Input
          placeholder={mobilePhonePlaceholder}
          value={mobilePhone.value}
          ref={mobilePhoneRef}
          onChange={handleInputChange(setMobilePhone)}
          invalid={mobilePhone.invalid}
          validations={[validPhone, validPhoneLength]}
          errorMessage={mobilePhone.errorMessage}
          mask={masks.phone}
          shouldValidate={mobilePhone.value}
        />

        <Input
          placeholder={commercialPhonePlaceholder}
          value={commercialPhone.value}
          ref={comercialPhoneRef}
          onChange={handleInputChange(setComercialPhone)}
          invalid={commercialPhone.invalid}
          validations={[validCommercialPhone, validCommercialPhoneLength]}
          errorMessage={commercialPhone.errorMessage}
          mask={masks.commercialPhone}
          shouldValidate={commercialPhone.value}
        />
      </Row>
      <Row columns={2}>
        <Input
          placeholder={residentialPhonePlaceholder}
          value={residentialPhone.value}
          ref={residentialPhoneRef}
          onChange={handleInputChange(setResidentialPhone)}
          invalid={residentialPhone.invalid}
          validations={[validHomePhone, validHomePhoneLength]}
          errorMessage={residentialPhone.errorMessage}
          mask={masks.homePhone}
          shouldValidate={residentialPhone.value}
        />

        <Input
          placeholder={emailPlaceholder}
          value={email.value}
          ref={emailRef}
          onChange={handleInputChange(setEmail)}
          invalid={email.invalid}
          validations={[required, validEmail]}
          errorMessage={email.errorMessage}
          maxLength={50}
        />
      </Row>
      <ContactDataFooter
        contactWay={contactData?.recebimento?.[0]}
        ref={contactDataFooterRef}
      />
    </Section>
  );
};

export default forwardRef(ContactData);
